/**
 * Планувальник: скільки нового давати сьогодні, що саме повторювати
 * і чи встигаємо до дедлайну (див. D-001, D-006).
 *
 * Логіка самолікувальна: план не «прибитий» до календаря наперед.
 * Норма нового перераховується щодня з залишку матеріалу і залишку днів,
 * тому пропущений день не ламає курс — він просто трохи піднімає норму.
 *
 * Модуль чистий: час приходить аргументом.
 */

import { FINAL_STRETCH_DAYS, compareQueue, isDue, type Card } from './srs.ts'
import { daysBetween } from './time.ts'

export interface PlannerConfig {
  /** Початок дня дедлайну, epoch ms. */
  deadline: number | null
  /** Скільки останніх днів іде без нового матеріалу. */
  finalStretchDays: number
  /** Стеля повторень за сесію, щоб день не перетворився на дві години. */
  maxReviewsPerSession: number
  /** Мінімальна порція нового, поки матеріал не вичерпано. */
  minNewPerDay: number
  /** Стеля нового за день — захист від «наздоганяння» на 90 карток. */
  maxNewPerDay: number
  /** Оцінка часу на одну картку, секунди. */
  reviewSeconds: number
  newSeconds: number
}

export const DEFAULT_CONFIG: Omit<PlannerConfig, 'deadline'> = {
  finalStretchDays: FINAL_STRETCH_DAYS,
  maxReviewsPerSession: 140,
  minNewPerDay: 4,
  maxNewPerDay: 32,
  reviewSeconds: 7,
  newSeconds: 26,
}

export interface PlanInput {
  now: number
  /** Усі картки курсу, включно з тими, що ще у стані `new`. */
  cards: Card[]
  /** Порядок введення нового матеріалу — id фраз у порядку курсу. */
  order: string[]
  /** Скільки нових карток уже введено сьогодні. */
  introducedToday: number
}

export interface Session {
  /** Картки на повторення, вже впорядковані. */
  due: Card[]
  /** id нових фраз, які треба ввести сьогодні. */
  fresh: string[]
  /** Денна норма нового за поточним розрахунком. */
  newPerDay: number
  /** Скільки нового ще лишилось у курсі. */
  newRemaining: number
  /** Прострочені понад стелю сесії — вони перейдуть на завтра. */
  backlog: number
  /** Днів до дедлайну. */
  daysLeft: number
  /** Днів, доки ще можна вводити новий матеріал. */
  introDaysLeft: number
  /** Оцінка тривалості сесії у хвилинах. */
  estimateMinutes: number
  /** Чи встигаємо за поточного темпу. */
  onTrack: boolean
}

/**
 * Денна норма нового матеріалу.
 *
 * Ділимо залишок на дні, що лишились до початку фінального прогону.
 * Якщо накопичився хвіст прострочених повторень — норму тимчасово зрізаємо,
 * інакше борг росте як снігова куля.
 */
export function newPerDay(
  newRemaining: number,
  introDaysLeft: number,
  backlogRatio: number,
  cfg: PlannerConfig,
): number {
  if (newRemaining <= 0) return 0
  if (introDaysLeft <= 0) return 0 // фінальний прогін: нового не даємо

  // backlogRatio = прострочені / стеля сесії. Понад 1 — борг уже не влазить у день.
  if (backlogRatio >= 1.5) return 0

  const capped = Math.min(Math.ceil(newRemaining / introDaysLeft), cfg.maxNewPerDay)
  if (backlogRatio >= 1) {
    // Наздоганяємо повторення, але не зупиняємо курс повністю.
    return Math.max(Math.min(cfg.minNewPerDay, capped), Math.ceil(capped * 0.5))
  }
  return capped
}

/** Збирає сесію на зараз. */
export function planSession(input: PlanInput, cfg: PlannerConfig): Session {
  const { now, cards, order, introducedToday } = input

  const byId = new Map(cards.map((c) => [c.id, c]))
  const dueAll = cards.filter((c) => isDue(c, now)).sort(compareQueue)
  const due = dueAll.slice(0, cfg.maxReviewsPerSession)
  const backlog = dueAll.length - due.length

  const untouched = order.filter((id) => (byId.get(id)?.state ?? 'new') === 'new')
  const newRemaining = untouched.length

  const daysLeft = cfg.deadline ? daysBetween(now, cfg.deadline) : Infinity
  const introDaysLeft = cfg.deadline
    ? Math.max(0, daysLeft - cfg.finalStretchDays)
    : Infinity

  const perDay =
    introDaysLeft === Infinity
      ? Math.min(cfg.maxNewPerDay, Math.max(cfg.minNewPerDay, 12))
      : newPerDay(newRemaining, introDaysLeft, dueAll.length / cfg.maxReviewsPerSession, cfg)

  const freshCount = Math.max(0, perDay - introducedToday)
  const fresh = untouched.slice(0, freshCount)

  const estimateMinutes = Math.round(
    (due.length * cfg.reviewSeconds + fresh.length * cfg.newSeconds) / 60,
  )

  // Встигаємо, якщо залишок нового реально розкладається по днях без перевищення стелі.
  const onTrack =
    introDaysLeft === Infinity || newRemaining === 0
      ? true
      : newRemaining <= introDaysLeft * cfg.maxNewPerDay

  return {
    due,
    fresh,
    newPerDay: perDay,
    newRemaining,
    backlog,
    daysLeft: daysLeft === Infinity ? Number.POSITIVE_INFINITY : daysLeft,
    introDaysLeft: introDaysLeft === Infinity ? Number.POSITIVE_INFINITY : introDaysLeft,
    estimateMinutes,
    onTrack,
  }
}

export interface DayPlan {
  /** 1-based номер дня курсу. */
  day: number
  dateKey: string
  /** Скільки нових фраз заплановано. */
  newCount: number
  /** id модулів, яких торкається цей день. */
  moduleIds: string[]
  /** Режим дня. */
  mode: 'new' | 'final' | 'exam'
}

/**
 * Прогноз розкладу на весь курс — для екрана «план».
 * Це орієнтир, а не жорсткий графік: реальна подача рахується щодня.
 */
export function projectSchedule(
  order: string[],
  moduleOf: (phraseId: string) => string,
  startDay: number,
  cfg: PlannerConfig,
  dayKeyOf: (dayIndex: number) => string,
): DayPlan[] {
  if (!cfg.deadline) return []
  const totalDays = Math.max(1, daysBetween(startDay, cfg.deadline))
  const introDays = Math.max(1, totalDays - cfg.finalStretchDays)
  const perDay = Math.min(cfg.maxNewPerDay, Math.ceil(order.length / introDays))

  const plans: DayPlan[] = []
  let cursor = 0
  for (let day = 1; day <= totalDays; day++) {
    const isFinal = day > introDays
    const slice = isFinal ? [] : order.slice(cursor, cursor + perDay)
    cursor += slice.length
    const moduleIds = [...new Set(slice.map(moduleOf))]
    plans.push({
      day,
      dateKey: dayKeyOf(day - 1),
      newCount: slice.length,
      moduleIds,
      mode: day === totalDays ? 'exam' : isFinal ? 'final' : 'new',
    })
  }
  return plans
}

/** Чи вміщується курс у дедлайн за заданої стелі нового на день. */
export function fitsDeadline(
  totalPhrases: number,
  startDay: number,
  cfg: PlannerConfig,
): { fits: boolean; requiredPerDay: number; introDays: number } {
  if (!cfg.deadline) return { fits: true, requiredPerDay: 0, introDays: Infinity }
  const totalDays = Math.max(1, daysBetween(startDay, cfg.deadline))
  const introDays = Math.max(1, totalDays - cfg.finalStretchDays)
  const requiredPerDay = Math.ceil(totalPhrases / introDays)
  return { fits: requiredPerDay <= cfg.maxNewPerDay, requiredPerDay, introDays }
}
